import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  FlagTriangleRight,
  LogIn,
  MapPin,
  UsersRound,
} from "lucide-react"

import { DemoNotice } from "@/components/demo-notice"
import { PublicPagePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { StatusChip } from "@/components/status-chip"
import { Button } from "@/components/ui/button"
import {
  dateInTimeZone,
  formatCourseDate,
  formatCurrency,
  formatMinutes,
} from "@/lib/date"
import { defaultDemoPath } from "@/lib/navigation"
import { myBookingsQueryOptions, viewerQueryOptions } from "@/lib/query-options"
import type { getMyBookingsFn } from "@/server/functions"

export const Route = createFileRoute("/my-bookings/")({
  loader: async ({ context }) => {
    const viewer =
      await context.queryClient.ensureQueryData(viewerQueryOptions())

    if (!viewer) {
      throw redirect({
        to: "/login",
        search: { next: "/my-bookings" },
      })
    }

    if (viewer.role !== "golfer") {
      throw redirect({ to: defaultDemoPath(viewer.id, viewer.role) })
    }

    const data = await context.queryClient.ensureQueryData(
      myBookingsQueryOptions()
    )

    return data
  },
  head: () => ({
    meta: [{ title: "My bookings · Groundline" }],
  }),
  pendingComponent: PublicPagePending,
  component: MyBookings,
})

function MyBookings() {
  const { data } = useSuspenseQuery(myBookingsQueryOptions())

  if (!data.viewer) {
    return (
      <>
        <PublicHeader viewer={null} />
        <DemoNotice />
        <main className="site-frame grid min-h-[70vh] place-items-center py-16">
          <div className="max-w-[560px] text-center">
            <LogIn className="mx-auto size-7 text-primary" />
            <h1 className="page-title">Log in to see your bookings.</h1>
            <p className="page-lede mx-auto">
              Use your Groundline account to view upcoming rounds and booking
              history.
            </p>
            <Button
              render={<Link to="/login" search={{ next: "/my-bookings" }} />}
              variant="accent"
              size="lg"
              className="mt-6"
            >
              Log in
              <ArrowRight data-icon="inline-end" />
            </Button>
          </div>
        </main>
      </>
    )
  }

  const today = dateInTimeZone()
  const upcoming = data.bookings.filter(
    (booking) =>
      booking.teeTime.date >= today &&
      booking.status !== "cancelled" &&
      booking.status !== "completed" &&
      booking.status !== "no_show"
  )
  const history = data.bookings.filter(
    (booking) =>
      !upcoming.some((upcomingBooking) => upcomingBooking.id === booking.id)
  )

  return (
    <>
      <PublicHeader viewer={data.viewer} />
      <DemoNotice />
      <main className="site-frame py-11 sm:py-15">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <span className="eyebrow">
              <CalendarDays className="size-4" />
              {data.viewer.name}
            </span>
            <h1 className="page-title">My bookings.</h1>
            <p className="page-lede">
              Every round here reads from the same booking ledger as the staff
              workspace.
            </p>
          </div>
          <Button render={<Link to="/" />} variant="accent">
            Book another round
            <ArrowRight data-icon="inline-end" />
          </Button>
        </div>

        <BookingSection
          title="Upcoming rounds"
          bookings={upcoming}
          emptyCopy="No active rounds yet. Choose a live tee time to create one."
        />

        {history.length > 0 ? (
          <BookingSection
            title="Booking history"
            bookings={history}
            emptyCopy=""
          />
        ) : null}
      </main>
    </>
  )
}

type BookingItem = Awaited<
  ReturnType<typeof getMyBookingsFn>
>["bookings"][number]

function BookingSection({
  title,
  bookings,
  emptyCopy,
}: {
  title: string
  bookings: Array<BookingItem>
  emptyCopy: string
}) {
  return (
    <section className="mt-9">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="section-title">{title}</h2>
        <span className="text-xs font-semibold text-muted-foreground">
          {bookings.length} {bookings.length === 1 ? "booking" : "bookings"}
        </span>
      </div>
      <div className="ruled-panel overflow-hidden">
        {bookings.length === 0 ? (
          <div className="empty-state">
            <div>
              <FlagTriangleRight className="size-6" />
              <strong>Nothing on the rail</strong>
              <p className="mt-2">{emptyCopy}</p>
              <Button
                render={<Link to="/" />}
                variant="outline"
                className="mt-5"
              >
                Browse tee times
              </Button>
            </div>
          </div>
        ) : (
          bookings.map((booking) => (
            <Link
              key={booking.id}
              to="/my-bookings/$bookingId"
              params={{ bookingId: booking.id }}
              className="rule-row grid gap-4 p-5 transition-colors hover:bg-[#f2f7f9] sm:grid-cols-[108px_1fr_auto] sm:items-center"
            >
              <div>
                <span className="block font-display text-[1.65rem] leading-none font-semibold">
                  {formatMinutes(booking.teeTime.startMinutes)}
                </span>
                <span className="mt-1.5 block text-[0.68rem] font-bold tracking-[0.08em] text-muted-foreground uppercase">
                  {formatCourseDate(booking.teeTime.date, {
                    weekday: "short",
                    month: "short",
                  })}
                </span>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-[1.35rem] leading-none font-semibold uppercase">
                    {booking.course.shortName}
                  </h3>
                  <StatusChip status={booking.status} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="size-3" />
                    {booking.course.location}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <UsersRound className="size-3" />
                    {booking.playerCount} players
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 className="size-3" />
                    {booking.reference}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 sm:justify-end">
                <strong className="text-sm">
                  {formatCurrency(booking.totalCents, booking.course.currency)}
                </strong>
                <span className="bg-paper grid size-9 place-items-center rounded-full border border-border text-muted-foreground">
                  <ArrowRight className="size-4" />
                </span>
              </div>
            </Link>
          ))
        )}
      </div>
    </section>
  )
}
